import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { useAuth } from "@/hooks/use-auth";
import { queryKeys } from "@/lib/query-keys";

export interface PersonalNote {
  id: string;
  title: string | null;
  content: string;
  updated_at: string;
}

/** Upper bound on personal notes fetched — avoids an unbounded scan. */
const NOTES_FETCH_CAP = 200;

export interface NotesQueryOptions {
  /** Cap the list (the sidebar shows five titles, not the whole archive). */
  limit?: number;
  /**
   * Skip the `content` column. The sidebar renders titles only; pulling the
   * body of up to 200 notes on every page was pure payload.
   */
  includeContent?: boolean;
  /** Skip the query — the sidebar's list is collapsed on most page loads. */
  enabled?: boolean;
}

/**
 * CRUD for standalone personal ("own") notes — free-form notes that are not
 * tied to a question or vocabulary item. Backed by the shared `notes` table
 * (item_type = 'own'), the same table that powers per-question/per-vocabulary
 * session notes (see use-session-notes.ts).
 */
export const usePersonalNotes = (options: NotesQueryOptions = {}) => {
  const { limit = NOTES_FETCH_CAP, includeContent = true, enabled = true } = options;
  const { userId, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const baseKey = queryKeys.notes.personal(userId);
  const listKey = [...baseKey, includeContent ? "full" : "meta", limit] as const;

  const query = useQuery({
    queryKey: listKey,
    enabled: enabled && !!userId && !!isAuthenticated,
    queryFn: async (): Promise<PersonalNote[]> => {
      // The select list is conditional, which the generated types cannot parse;
      // cast the builder (the same pattern used elsewhere for dynamic selects).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (db as any).from("notes")
        .select(includeContent ? "id, title, content, updated_at" : "id, title, updated_at")
        .eq("user_id", userId!)
        .eq("item_type", "own")
        .order("updated_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return ((data || []) as Array<Partial<PersonalNote> & { id: string; updated_at: string }>)
        .map((note) => ({ ...note, content: note.content ?? "" }) as PersonalNote);
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: baseKey });

  const createNote = useMutation({
    mutationFn: async (input: { title?: string; content?: string }) => {
      const { data, error } = await db.from("notes")
        .insert({
          user_id: userId!,
          item_type: "own",
          title: input.title?.trim() || null,
          content: input.content || "",
        })
        .select("id, title, content, updated_at")
        .single();

      if (error) throw error;
      return data as PersonalNote;
    },
    onSuccess: invalidate,
  });

  const updateNote = useMutation({
    // Only the fields present in `input` are written, so saving the body never
    // clobbers the title (and vice versa) using a stale value of the other.
    mutationFn: async (input: { id: string; title?: string; content?: string }) => {
      const payload: { title?: string | null; content?: string } = {};
      if ("title" in input) payload.title = input.title?.trim() || null;
      if ("content" in input) payload.content = input.content ?? "";

      const { error } = await db.from("notes")
        .update(payload)
        .eq("id", input.id)
        .eq("user_id", userId!);

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("notes")
        .delete()
        .eq("id", id)
        .eq("user_id", userId!);

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    notes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isAuthenticated: !!isAuthenticated,
    createNote,
    updateNote,
    deleteNote,
  };
};
